import React from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Download, ClipboardCopy } from "lucide-react";
import { toast } from "sonner";
import { AdminVehicleOverride } from "./adminStore";
import { ImageFieldAdmin } from "./ImageFieldAdmin";

interface VehicleManagerProps {
  vehicles: AdminVehicleOverride[];
  onChange: (vehicles: AdminVehicleOverride[]) => void;
}

const emptyVehicle = (): AdminVehicleOverride => ({
  id: Date.now(),
  title: "Nuevo vehículo",
  price: "L 0",
  location: "",
  mileage: "",
  badge: "",
  url: "https://motorproautos.com/explore",
  image: "",
  imagePath: "src/assets/vehicles/nuevo-vehiculo.jpg",
});

export const VehicleManager: React.FC<VehicleManagerProps> = ({ vehicles, onChange }) => {
  const updateVehicle = (id: number, patch: Partial<AdminVehicleOverride>) => {
    onChange(vehicles.map((v) => (v.id === id ? { ...v, ...patch } : v)));
  };

  const removeVehicle = (id: number) => {
    onChange(vehicles.filter((v) => v.id !== id));
  };

  const moveVehicle = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= vehicles.length) return;
    const next = [...vehicles];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const addVehicle = () => onChange([...vehicles, emptyVehicle()]);

  const exportVehicles = () => {
    const blob = new Blob([JSON.stringify(vehicles, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "featuredVehicles.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyVehiclesJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(vehicles, null, 2));
      toast.success("Configuración copiada");
    } catch {
      toast.error("No se pudo copiar la configuración");
    }
  };

  return (
    <div data-testid="vehicle-manager" className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs font-mono text-neutral-500">
          Archivo permanente: src/data/featuredVehicles.ts
        </p>
        <div className="flex gap-2">
          <button
            data-testid="vehicle-manager-add-button"
            onClick={addVehicle}
            className="inline-flex items-center gap-1.5 rounded-full bg-motor-ink px-4 py-2 text-xs font-semibold text-white hover:bg-black transition-colors"
          >
            <Plus className="h-3.5 w-3.5" /> Agregar vehículo
          </button>
          <button
            data-testid="vehicle-manager-copy-button"
            onClick={copyVehiclesJson}
            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 px-4 py-2 text-xs font-semibold text-motor-ink hover:border-motor-red hover:text-motor-red transition-colors"
          >
            <ClipboardCopy className="h-3.5 w-3.5" /> Copiar JSON
          </button>
          <button
            data-testid="vehicle-manager-export-button"
            onClick={exportVehicles}
            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 px-4 py-2 text-xs font-semibold text-motor-ink hover:border-motor-red hover:text-motor-red transition-colors"
          >
            <Download className="h-3.5 w-3.5" /> Exportar vehículos (JSON)
          </button>
        </div>
      </div>

      <div className="grid gap-3">
        {vehicles.map((vehicle, index) => (
          <div
            key={vehicle.id}
            data-testid={`vehicle-manager-item-${vehicle.id}`}
            className="grid gap-4 rounded-xl border border-neutral-200 p-4"
          >
            <div className="grid sm:grid-cols-[1fr_auto] gap-4">
            <div className="grid grid-cols-2 gap-2.5">
              <input
                data-testid={`vehicle-title-${vehicle.id}`}
                value={vehicle.title}
                onChange={(e) => updateVehicle(vehicle.id, { title: e.target.value })}
                placeholder="Título (marca, modelo, año)"
                className="col-span-2 rounded-lg border border-neutral-300 px-3 py-2 text-sm"
              />
              <input
                data-testid={`vehicle-price-${vehicle.id}`}
                value={vehicle.price}
                onChange={(e) => updateVehicle(vehicle.id, { price: e.target.value })}
                placeholder="Precio"
                className="rounded-lg border border-neutral-300 px-3 py-2 text-sm"
              />
              <input
                data-testid={`vehicle-location-${vehicle.id}`}
                value={vehicle.location}
                onChange={(e) => updateVehicle(vehicle.id, { location: e.target.value })}
                placeholder="Ubicación"
                className="rounded-lg border border-neutral-300 px-3 py-2 text-sm"
              />
              <input
                data-testid={`vehicle-mileage-${vehicle.id}`}
                value={vehicle.mileage}
                onChange={(e) => updateVehicle(vehicle.id, { mileage: e.target.value })}
                placeholder="Millaje (opcional)"
                className="rounded-lg border border-neutral-300 px-3 py-2 text-sm"
              />
              <input
                data-testid={`vehicle-badge-${vehicle.id}`}
                value={vehicle.badge}
                onChange={(e) => updateVehicle(vehicle.id, { badge: e.target.value })}
                placeholder="Etiqueta (opcional)"
                className="rounded-lg border border-neutral-300 px-3 py-2 text-sm"
              />
              <input
                data-testid={`vehicle-url-${vehicle.id}`}
                value={vehicle.url}
                onChange={(e) => updateVehicle(vehicle.id, { url: e.target.value })}
                placeholder="URL del anuncio"
                className="col-span-2 rounded-lg border border-neutral-300 px-3 py-2 text-sm font-mono text-xs"
              />
            </div>

            <div className="flex sm:flex-col gap-1.5 justify-end">
              <button
                data-testid={`vehicle-move-up-${vehicle.id}`}
                onClick={() => moveVehicle(index, -1)}
                disabled={index === 0}
                className="h-9 w-9 flex items-center justify-center rounded-full border border-neutral-200 disabled:opacity-30"
                aria-label="Mover arriba"
              >
                <ArrowUp className="h-4 w-4" />
              </button>
              <button
                data-testid={`vehicle-move-down-${vehicle.id}`}
                onClick={() => moveVehicle(index, 1)}
                disabled={index === vehicles.length - 1}
                className="h-9 w-9 flex items-center justify-center rounded-full border border-neutral-200 disabled:opacity-30"
                aria-label="Mover abajo"
              >
                <ArrowDown className="h-4 w-4" />
              </button>
              <button
                data-testid={`vehicle-delete-${vehicle.id}`}
                onClick={() => removeVehicle(vehicle.id)}
                className="h-9 w-9 flex items-center justify-center rounded-full border border-neutral-200 text-motor-red hover:bg-motor-red hover:text-white transition-colors"
                aria-label="Eliminar vehículo"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            </div>

            <ImageFieldAdmin
              image={{
                key: `vehicle.${vehicle.id}`,
                label: "Imagen del vehículo",
                path: vehicle.imagePath,
                src: vehicle.image,
                recommendedSize: "1000×800 px",
                recommendedFormat: "JPG",
                alt: vehicle.title,
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
